import { CalendarBlank, Clock } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Props = {
	value: Date | null;
	onChange: (value: Date | null) => void;
	minDate?: Date;
	placeholder?: string;
	className?: string;
	id?: string;
	"aria-invalid"?: boolean;
};

function startOfDay(d: Date): Date {
	const x = new Date(d);
	x.setHours(0, 0, 0, 0);
	return x;
}

function clampToMin(d: Date, min?: Date): Date {
	if (!min) return d;
	return d.getTime() < min.getTime() ? new Date(min) : d;
}

export function DateTimePicker({
	value,
	onChange,
	minDate,
	placeholder,
	className,
	id,
	...ariaProps
}: Props) {
	const { t, i18n } = useTranslation();
	const [open, setOpen] = useState(false);

	const formatter = useMemo(
		() =>
			new Intl.DateTimeFormat(i18n.language, {
				dateStyle: "medium",
				timeStyle: "short",
			}),
		[i18n.language],
	);

	const label = value
		? formatter.format(value)
		: (placeholder ?? t("datetimePicker.selectDateTime"));

	const minCalendarDate = minDate ? startOfDay(minDate) : undefined;

	function handleDaySelect(day: Date | undefined) {
		if (!day) return;
		const next = new Date(day);
		const base = value ?? minDate ?? new Date();
		next.setHours(base.getHours(), base.getMinutes(), 0, 0);
		onChange(clampToMin(next, minDate));
	}

	function handleTimeChange(hours: number, minutes: number) {
		const base = value ?? minDate ?? new Date();
		const next = new Date(base);
		next.setSeconds(0, 0);
		next.setHours(hours, minutes);
		onChange(clampToMin(next, minDate));
	}

	function handleSetNow() {
		onChange(new Date());
	}

	const hours = value?.getHours() ?? 0;
	const minutes = value?.getMinutes() ?? 0;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="outline"
					className={cn(
						"justify-start font-normal",
						!value && "text-muted-foreground",
						className,
					)}
					aria-invalid={ariaProps["aria-invalid"]}
				>
					<CalendarBlank className="size-4" />
					{label}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					mode="single"
					selected={value ?? undefined}
					onSelect={handleDaySelect}
					defaultMonth={value ?? minDate ?? undefined}
					disabled={minCalendarDate ? { before: minCalendarDate } : undefined}
				/>
				<div className="flex items-center gap-2 border-t p-3">
					<Clock className="text-muted-foreground size-4" aria-hidden="true" />
					<Input
						type="number"
						min={0}
						max={23}
						step={1}
						value={value ? String(hours).padStart(2, "0") : ""}
						placeholder="HH"
						aria-label={t("datetimePicker.hours")}
						className="w-16 text-center"
						onChange={(e) => {
							const raw = Number.parseInt(e.target.value, 10);
							if (Number.isNaN(raw)) return;
							const h = Math.max(0, Math.min(23, raw));
							handleTimeChange(h, minutes);
						}}
					/>
					<span className="text-muted-foreground">:</span>
					<Input
						type="number"
						min={0}
						max={59}
						step={1}
						value={value ? String(minutes).padStart(2, "0") : ""}
						placeholder="MM"
						aria-label={t("datetimePicker.minutes")}
						className="w-16 text-center"
						onChange={(e) => {
							const raw = Number.parseInt(e.target.value, 10);
							if (Number.isNaN(raw)) return;
							const m = Math.max(0, Math.min(59, raw));
							handleTimeChange(hours, m);
						}}
					/>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="ml-auto"
						onClick={handleSetNow}
					>
						{t("datetimePicker.now")}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
